import axios from "axios";
import {
  GENERIC_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
} from "./constants/errorMessages";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

let isRefreshing = false;
let refreshSubscribers = [];

const PUBLIC_AUTH_ROUTES = [
  "/auth/login",
  "/auth/refresh",
  "/auth/request-reset",
  "/auth/reset-password",
];

const isPublicAuthRoute = (url = "") =>
  PUBLIC_AUTH_ROUTES.some((route) => url.startsWith(route));

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed() {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
}

// Backstop for the whole portal. Screens read err.response.data.error /
// .message / .details, so rewriting those on a server fault means no internal
// text can reach a banner or toast even if a route slips through. The original
// response is left on error.serverResponse for debugging.
const sanitizeServerError = (error) => {
  if (!error.response) {
    error.message = NETWORK_ERROR_MESSAGE;
    return error;
  }

  if (error.response.status >= 500) {
    error.serverResponse = error.response.data;
    error.response.data = {
      ...(typeof error.response.data === "object" ? error.response.data : {}),
      error: GENERIC_ERROR_MESSAGE,
      message: GENERIC_ERROR_MESSAGE,
      details: undefined,
    };
    error.message = GENERIC_ERROR_MESSAGE;
  }

  return error;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !isPublicAuthRoute(originalRequest.url) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(() => {
            api(originalRequest).then(resolve).catch(reject);
          });
        });
      }

      isRefreshing = true;

      try {
        await api.post("/auth/refresh", {});
        isRefreshing = false;
        onRefreshed();
        return api(originalRequest);
      } catch (err) {
        isRefreshing = false;
        refreshSubscribers = [];
        return Promise.reject(sanitizeServerError(err));
      }
    }

    return Promise.reject(sanitizeServerError(error));
  }
);

export default api;
